import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { BarChart3, Image, Users, Mail, FileText, TrendingUp, Clock, CheckCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import useAdmissionStore from '../../stores/admissionStore';
import useMessageStore from '../../stores/messageStore';

const Dashboard = () => {
  const { user } = useAuth();
  const { applications, fetchApplications, getStats: getAdmissionStats } = useAdmissionStore();
  const { messages, fetchMessages, getStats: getMessageStats } = useMessageStore();
  
  const [loading, setLoading] = useState(true);
  const hasFetched = useRef(false);

  useEffect(() => {
    // Prevent duplicate fetches in React Strict Mode
    if (hasFetched.current) return;
    hasFetched.current = true;

    const loadData = async () => {
      await Promise.all([
        fetchApplications(),
        fetchMessages()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchApplications, fetchMessages]);

  const admissionStats = getAdmissionStats();
  const messageStats = getMessageStats();

  const stats = [
    {
      title: 'Total Admissions',
      value: admissionStats.total,
      change: `${admissionStats.pending} pending`,
      icon: FileText,
      color: 'bg-blue-500',
      link: '/admin/admissions'
    },
    {
      title: 'Messages',
      value: messageStats.total,
      change: `${messageStats.unread} unread`,
      icon: Mail,
      color: 'bg-green-500',
      link: '/admin/messages'
    },
    {
      title: 'Accepted Applications',
      value: admissionStats.approved,
      change: `${Math.round((admissionStats.approved / admissionStats.total) * 100) || 0}% acceptance rate`,
      icon: CheckCircle,
      color: 'bg-purple-500',
      link: '/admin/admissions'
    },
    {
      title: 'Starred Messages',
      value: messageStats.starred,
      change: 'Important items',
      icon: TrendingUp,
      color: 'bg-yellow-500',
      link: '/admin/messages'
    }
  ];

  const quickActions = [
    {
      title: 'Manage Slider',
      description: 'Update hero slider images',
      icon: Image,
      link: '/admin/slider',
      color: 'bg-indigo-100 text-indigo-600'
    },
    {
      title: 'Gallery Management',
      description: 'Upload and organize photos',
      icon: Image,
      link: '/admin/gallery',
      color: 'bg-pink-100 text-pink-600'
    },
    {
      title: 'View Admissions',
      description: 'Review new applications',
      icon: FileText,
      link: '/admin/admissions',
      color: 'bg-blue-100 text-blue-600'
    },
    {
      title: 'Alumni Management',
      description: 'Manage success stories',
      icon: Users,
      link: '/admin/alumni',
      color: 'bg-green-100 text-green-600'
    }
  ];

  const recentActivity = [
    ...applications.slice(0, 3).map(app => ({
      type: 'admission',
      title: `New admission: ${app.studentName}`,
      time: new Date(app.appliedDate).toLocaleDateString(),
      status: app.status
    })),
    ...messages.slice(0, 2).map(msg => ({
      type: 'message',
      title: `Message from ${msg.name}`,
      time: new Date(msg.createdAt).toLocaleDateString(),
      status: msg.status
    }))
  ].slice(0, 5);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-purple"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-primary-purple to-primary-magenta rounded-xl p-6 text-white">
        <h1 className="text-3xl font-bold mb-2">Welcome back, {user?.name || 'Admin'}!</h1>
        <p className="text-purple-100">Here's what's happening with your school today.</p>
      </div>

      {/* Statistics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <Link
            key={index}
            to={stat.link}
            className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow p-6 border border-gray-100"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`${stat.color} p-3 rounded-lg`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
            </div>
            <h3 className="text-gray-600 text-sm font-medium mb-1">{stat.title}</h3>
            <p className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</p>
            <p className="text-sm text-gray-500">{stat.change}</p>
          </Link>
        ))}
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {quickActions.map((action, index) => (
            <Link
              key={index}
              to={action.link}
              className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all p-6 border border-gray-100 hover:border-primary-purple group"
            >
              <div className={`${action.color} p-3 rounded-lg w-fit mb-3 group-hover:scale-110 transition-transform`}>
                <action.icon className="w-6 h-6" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">{action.title}</h3>
              <p className="text-sm text-gray-500">{action.description}</p>
            </Link>
          ))}
        </div>
      </div>

      {/* Recent Activity */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-4">Recent Activity</h2>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          {recentActivity.length > 0 ? (
            <div className="divide-y divide-gray-100">
              {recentActivity.map((activity, index) => (
                <div key={index} className="p-4 hover:bg-gray-50 transition-colors flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${activity.type === 'admission' ? 'bg-blue-100' : 'bg-green-100'}`}>
                      {activity.type === 'admission' ? (
                        <FileText className={`w-4 h-4 ${activity.type === 'admission' ? 'text-blue-600' : 'text-green-600'}`} />
                      ) : (
                        <Mail className="w-4 h-4 text-green-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{activity.title}</p>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Clock className="w-3 h-3" />
                        <span>{activity.time}</span>
                      </div>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    activity.status === 'Pending' || activity.status === 'New' 
                      ? 'bg-yellow-100 text-yellow-700' 
                      : activity.status === 'Accepted' || activity.status === 'Resolved'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-700'
                  }`}>
                    {activity.status}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center text-gray-500">
              <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No recent activity</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
